import { useEffect } from "react";
import { useImmer } from "use-immer";
import type { ElkNode } from "elkjs";
import type {
  GraphGeometry,
  LayoutBox,
  LayoutConnection,
} from "./general-layout";
import { type LayoutGroup, layoutGroupedGraph } from "./grouped-layout";

export interface LayoutExecutor {
  execute: (graph: ElkNode) => Promise<ElkNode>;
  dispose: () => void;
}
export interface GraphLayoutRequest {
  boxes: LayoutBox[];
  edges: LayoutConnection[];
  groups: LayoutGroup[];
  graphKey: string;
  sharedTailNodeIds?: string[];
}
export async function createGraphLayoutExecutor(): Promise<LayoutExecutor> {
  // The same engine runs in non-browser consumers. Browser failures remain errors.
  if (typeof Worker === "undefined") {
    const { default: ELK } = await import("elkjs/lib/elk.bundled.js");
    const elk = new ELK();
    return { execute: (graph) => elk.layout(graph), dispose: () => {} };
  }
  const { createLayoutWorker } = await import("./layout-worker-client");
  return createLayoutWorker();
}

export function useRuleGraphLayout(
  request: GraphLayoutRequest,
  createExecutor: () => Promise<LayoutExecutor>
) {
  const key = JSON.stringify(request);
  const [attempt, setAttempt] = useImmer(0);
  const [state, setState] = useImmer<{
    key: string;
    graphKey: string;
    geometry: GraphGeometry | null;
    error: boolean;
  }>({ key: "", graphKey: "", geometry: null, error: false });
  useEffect(() => {
    let active = true;
    let executor: LayoutExecutor | undefined;
    const snapshot: GraphLayoutRequest = JSON.parse(key);
    void (async () => {
      try {
        executor = await createExecutor();
        if (!active) {
          executor.dispose();
          return;
        }
        const geometry = await layoutGroupedGraph(
          snapshot.boxes,
          snapshot.edges,
          snapshot.groups,
          executor.execute,
          snapshot.sharedTailNodeIds
        );
        if (active)
          setState({
            key,
            graphKey: snapshot.graphKey,
            geometry,
            error: false,
          });
      } catch {
        if (active)
          setState({
            key,
            graphKey: snapshot.graphKey,
            geometry: null,
            error: true,
          });
      }
    })();
    return () => {
      active = false;
      executor?.dispose();
    };
  }, [key, attempt, createExecutor, setState]);
  const current = state.key === key;
  return {
    geometry: state.graphKey === request.graphKey ? state.geometry : null,
    ready: current && state.geometry !== null,
    error: current && state.error,
    revision: state.geometry === null ? "" : state.key,
    retry: () => {
      setState((draft) => {
        draft.error = false;
      });
      setAttempt((value) => value + 1);
    },
  };
}
